// "Callwright" topbar menu — brand label is the trigger, active project
// shows as a small subtitle directly below the brand. Opens a dropdown
// grouped by workspace.
//
// Per-workspace header row (one per workspace group):
//   "{Workspace name} ({role})  [⚙ settings]  [+ new workspace]"
// Gear is admin+ only; "+ new workspace" is always available (it's a
// global action the user kept side-by-side per the redesign request).
// A Menu.Separator divides each workspace from the next.
//
// Per-project row: project name + green ✓ + ⚙ (active row only).
// Each workspace section ends with a "+ New project" row (admin+).

import {
  Box,
  Button,
  HStack,
  IconButton,
  Menu,
  Portal,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiCheck, FiChevronDown, FiSettings, FiSliders } from "react-icons/fi";
import { useMe } from "@/hooks/useAuth";
import {
  useMyWorkspaces,
  useSetActiveProject,
} from "@/hooks/useWorkspaces";

interface Props {
  /** Open the per-project settings modal (lives in AppShell). */
  onOpenProjectSettings: () => void;
}

// Sentinel value prefix for "create project in workspace X" Menu.Items —
// distinct from any real project id so a project named after the sentinel
// can't collide. New-workspace / workspace-settings used to be separate
// sentinels but are now inline IconButtons in each workspace header.
// "New project" / "New workspace" were removed entirely in Phase B —
// workspace + project creation is now admin-only (paid setup).

export default function WorkspaceProjectSwitcher({ onOpenProjectSettings }: Props) {
  const { data: me } = useMe();
  const { data, isLoading } = useMyWorkspaces();
  const setActive = useSetActiveProject();
  const navigate = useNavigate();
  // Controlled menu so the gear click can close it while opening the modal.
  const [open, setOpen] = useState(false);

  if (!me) return null;

  const activeProjectId = me.lastActiveProjectId;
  const activeWs = data?.workspaces.find((w) =>
    w.projects.some((p) => p.id === activeProjectId),
  );
  const activeProj = activeWs?.projects.find((p) => p.id === activeProjectId);

  function handleSelect(value: string | undefined) {
    if (!value) return;
    if (value === activeProjectId) return;
    setActive.mutate(value);
  }

  return (
    <>
      <Menu.Root
        open={open}
        onOpenChange={(d) => setOpen(d.open)}
        positioning={{ placement: "bottom-start" }}
        onSelect={(d) => handleSelect(d.value)}
      >
        <Menu.Trigger asChild>
          <Button
            variant="ghost"
            // Default internal padding kept (button rectangle stays a normal
            // button-shape on hover/focus). Outer alignment with page content
            // is handled by the topbar's px in AppShell — its left edge
            // matches the page content's left edge below.
            // Height auto so the two-line brand+subtitle stack fits.
            h="auto"
            py={1}
            loading={setActive.isPending}
          >
            <HStack gap={2} maxW={{ base: "14rem", sm: "20rem", md: "28rem" }}>
              <Stack gap={0} align="flex-start">
                <Text
                  fontWeight="semibold"
                  fontSize={{ base: "md", md: "lg" }}
                  whiteSpace="nowrap"
                  lineHeight="1.2"
                >
                  Callwright
                </Text>
                {activeProj ? (
                  <Text
                    fontSize="2xs"
                    color="fg.muted"
                    truncate
                    maxW={{ base: "12rem", sm: "16rem", md: "22rem" }}
                    lineHeight="1.2"
                  >
                    {activeProj.name}
                  </Text>
                ) : (
                  <Text fontSize="2xs" color="fg.muted" lineHeight="1.2">
                    No project
                  </Text>
                )}
              </Stack>
              <FiChevronDown />
            </HStack>
          </Button>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content
              minW={{ base: "16rem", sm: "20rem" }}
              maxH="28rem"
              overflowY="auto"
            >
              {isLoading || !data ? (
                <HStack p={3} gap={2}>
                  <Spinner size="xs" />
                  <Text fontSize="sm" color="fg.muted">
                    Loading workspaces…
                  </Text>
                </HStack>
              ) : data.workspaces.length === 0 ? (
                <Menu.Item value="" disabled>
                  You're not in any workspaces yet.
                </Menu.Item>
              ) : (
                data.workspaces.map((ws, wsIdx) => {
                  // Admins + owners can create projects + open workspace
                  // settings; viewers can only switch projects.
                  const canManage = ws.role === "OWNER" || ws.role === "ADMIN";
                  return (
                    <Menu.ItemGroup key={ws.id}>
                      {/* Separator between workspaces (not before the first). */}
                      {wsIdx > 0 ? <Menu.Separator /> : null}

                      {/* Header row: workspace name + role + inline icons.
                          Icons live INSIDE the ItemGroupLabel so the menu
                          tree stays well-formed; each one stopPropagation's
                          so a click can't bubble into Menu.Root's onSelect. */}
                      <Menu.ItemGroupLabel>
                        <HStack
                          flex="1"
                          justify="space-between"
                          align="center"
                          gap={2}
                        >
                          <HStack gap={1.5} minW={0} flex="1">
                            <Text truncate fontSize="sm" fontWeight="semibold">
                              {ws.name}
                            </Text>
                            <Text
                              as="span"
                              fontSize="xs"
                              color="fg.muted"
                              fontWeight="normal"
                              whiteSpace="nowrap"
                            >
                              ({ws.role.toLowerCase()})
                            </Text>
                          </HStack>
                          <HStack gap={0.5}>
                            {canManage ? (
                              <IconButton
                                aria-label="Workspace settings"
                                title="Workspace settings"
                                size="xs"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setOpen(false);
                                  // Workspace settings page reads the
                                  // ACTIVE workspace. If the gear was
                                  // clicked on a non-active workspace,
                                  // hop to it first via its first project.
                                  const firstProj = ws.projects.find(
                                    (p) => !p.archivedAt,
                                  );
                                  if (
                                    firstProj &&
                                    firstProj.id !== activeProjectId
                                  ) {
                                    setActive.mutate(firstProj.id, {
                                      onSuccess: () =>
                                        navigate("/workspace/settings"),
                                    });
                                  } else {
                                    navigate("/workspace/settings");
                                  }
                                }}
                              >
                                <FiSliders />
                              </IconButton>
                            ) : null}
                            {/* Phase B: "+ New workspace" removed —
                                workspace creation is now an admin-only
                                onboarding step (paid setup service).
                                Customers contact support to add a new
                                workspace. */}
                          </HStack>
                        </HStack>
                      </Menu.ItemGroupLabel>

                      {ws.projects
                        .filter((p) => !p.archivedAt)
                        .map((p) => {
                          const isActive = p.id === activeProjectId;
                          return (
                            <Menu.Item key={p.id} value={p.id}>
                              <HStack flex="1" justify="space-between" gap={2}>
                                <HStack gap={1.5} minW={0} flex="1">
                                  <Text truncate>{p.name}</Text>
                                  {isActive ? (
                                    <Box
                                      as="span"
                                      color="green.500"
                                      fontWeight="bold"
                                      display="inline-flex"
                                      alignItems="center"
                                    >
                                      <FiCheck strokeWidth={3} />
                                    </Box>
                                  ) : null}
                                </HStack>
                                {isActive ? (
                                  <IconButton
                                    aria-label="Project settings"
                                    title="Project settings"
                                    size="xs"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setOpen(false);
                                      onOpenProjectSettings();
                                    }}
                                  >
                                    <FiSettings />
                                  </IconButton>
                                ) : null}
                              </HStack>
                            </Menu.Item>
                          );
                        })}
                      {/* Phase B: "+ New project" removed for the same
                          reason — telephony setup (Telnyx + Retell
                          binding) requires admin assistance, so new
                          projects come through the admin path. */}
                    </Menu.ItemGroup>
                  );
                })
              )}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </>
  );
}
